/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import LoadingIndicator from "./LoadingIndicator";
import FormatTime from "./formatters/FormatTime";
import T from "./ui/Translation";
import { useClickableA11y } from "../hooks/a11y/useClickableA11y";
import { useConfig } from "../lib/react-query/useConfig";

export const StyledTimeSlotItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.625rem;
  padding: 0 1rem;
  font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
  border: 1px solid rgba(${({ theme }) => theme.colors.iconStroke.rgba});
  border-radius: 50px;
  background: rgba(${({ theme }) => theme.colors.background.rgba});
  color: rgba(${({ theme }) => theme.colors.text.rgba});
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;

  ${({ $selected }) => $selected && css`
    border-color: rgba(${({ theme }) => theme.colors.accent.rgba});
    background: rgba(${({ theme }) => theme.colors.accent.rgba});
    color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
  `}

  @media (hover: hover) {
    &:hover {
      border-color: rgba(${({ theme }) => theme.colors.accent.rgba});
      background: rgba(${({ theme }) => theme.colors.accent.rgba});
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
    }
  }

  ${({ $disabled }) => $disabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `}

  ${({ $withCounts }) => $withCounts && css`
    flex-direction: column;
  `}

  svg {
    stroke: currentColor;
  }
`;

const FEW_SPOTS_LEFT_RATIO = 0.25;

const SpotsLeft = styled.span`
  font-size: 0.75rem;
  color: rgba(${({ $few, theme }) => $few ? theme.colors.error.rgba : theme.colors.textSecondary.rgba});

  ${StyledTimeSlotItem}[aria-pressed="true"] &,
  ${StyledTimeSlotItem}:hover & {
    color: inherit;
  }
`;

const TimeSlotItem = ({ slot, isSelected, isPending, disabled, onClick }) => {
  const handleClick = () => {
    if (disabled) return;
    onClick?.(slot.time);
  };

  const a11yProps = useClickableA11y({ onClick: handleClick, disabled, selected: isSelected });
  const { data: config } = useConfig();
  const withCounts = slot.available_count != null;
  const fewSpotsLeft = slot.total_count != null && slot.available_count < slot.total_count * FEW_SPOTS_LEFT_RATIO;

  return (
    <StyledTimeSlotItem
      onClick={handleClick}
      {...a11yProps}
      $selected={isSelected}
      $disabled={disabled}
      $withCounts={withCounts}
    >
      {isPending && <LoadingIndicator size="sm" />}
      {!isPending && <FormatTime value={slot.time} />}
      {!isPending && withCounts && (
        <SpotsLeft $few={fewSpotsLeft}>
          {config?.slots_count_show_total && slot.total_count != null ? (
            <T available={slot.available_count} total={slot.total_count}>%available% of %total% left</T>
          ) : (
            <T available={slot.available_count}>%available% left</T>
          )}
        </SpotsLeft>
      )}
    </StyledTimeSlotItem>
  );
};

export default TimeSlotItem;
